import type React from "react";
import { useTranslation } from "react-i18next";
import ScalingLogoLarge from "../icons/ScalingLogoLarge";
import loginBg from "/login-bg.png";
import { LanguageSelector } from "~/molecules/LanguageSelector";

interface Props extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  boldText?: string;
}

export const AuthLayout = ({
  title,
  description,
  icon,
  boldText,
  children,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen w-full">
      {/* Main content container - full width on mobile, half on desktop */}
      <div className="relative flex w-full flex-col justify-center px-3 py-6 sm:px-6 sm:py-12 lg:w-1/2 lg:px-20">
        <div className="absolute top-4 right-3 left-3 flex justify-center sm:top-8 sm:right-6 sm:left-6 sm:w-80 sm:justify-start lg:right-auto lg:left-20">
          <div className="w-full max-w-50 sm:max-w-60 md:max-w-70 lg:max-w-none">
            <div className="h-auto w-full">
              <ScalingLogoLarge />
              <LanguageSelector />
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[20rem] pt-14 sm:max-w-md sm:pt-20">
          <div>
            {icon}
            {title && (
              <h2 className="mt-6 text-2xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-3 text-sm leading-6 text-gray-700 sm:mt-5 sm:text-base">
                {description}
                <br />{" "}
                {boldText && (
                  <strong className="font-medium">{boldText}</strong>
                )}
              </p>
            )}
          </div>

          <div className="mt-4 sm:mt-6">
            <div>{children}</div>
          </div>
        </div>
        <p className="absolute bottom-3 left-0 w-full px-3 text-center text-xs text-gray-400 sm:bottom-8 sm:px-6 sm:text-sm lg:px-20">
          © {new Date().getFullYear()} {t(($) => $.commons.copyright)}
        </p>
      </div>
      {/* Background image - hidden on mobile, shown on desktop */}
      <div className="relative hidden w-1/2 lg:block">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src={loginBg}
          alt="background"
        />
      </div>
    </div>
  );
};
